import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { Icon } from '@gumtree/ui-library';
// import { buildAdjustDeepLink } from '@gumtree/shared/src/util/adjust-deep-link';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import {
    BannerContainer,
    BannerCloseButton,
    BannerContentWrap,
    BannerContent,
    AppIcon,
    BannerText,
    BannerTitle,
    BannerSubtitle,
    GetButton,
    gumtreeIconStyle,
} from './app-download-banner.style';
import { getBannerConfig, mapGa4PageTypeToRouteType } from './app-download-banner-config';

interface AppDownloadBannerProps {
    /**
     * Override automatic configuration detection
     * If provided, these props will override the page-based configuration
     */
    nav?: string;
    campaign?: string;
    adgroup?: string;
    creative?: string;
    storageKey?: string;
    /**
     * Banner title text
     */
    title?: string;
    /**
     * Banner subtitle/description text
     */
    subtitle?: string;
}

const AppDownloadBanner: React.FC<AppDownloadBannerProps> = ({
    title = 'Gumtree - Buy & Sell',
    subtitle = 'Open in the Gumtree app',
}) => {
    
    const { baseConfig, adDetails } = useSelector((state: any) => ({
        baseConfig: state.baseConfig,
        adDetails: state.adDetails, // VIP page ad details
    }));
    const isMobile = baseConfig?.device?.type === 'mobile';
    const ga4PageType = baseConfig?.pageType;
    const isSafari = baseConfig?.isSafari ?? false;
    const deviceOS = baseConfig?.device?.os; // Get device OS (iOS, Android, etc.)

    const routePageType = mapGa4PageTypeToRouteType(ga4PageType);
    const pageConfig = routePageType ? getBannerConfig(routePageType) : undefined;

    /**
     * Get experiment variant value if experiment is configured for this page
     * This hook must be called unconditionally at component top level
     */
    const experimentVariant = useFeatureValue(
        pageConfig?.experiment?.featureKey || '',
        '' // default value
    );

    /**
     * Check if the banner should be enabled based on experiment configuration
     * If no experiment is configured for the page, return true (always enabled)
     * If experiment is configured, check if current variant matches enabled variants
     */
    const isExperimentEnabled = !pageConfig?.experiment || 
        pageConfig.experiment.enabledVariants.includes(experimentVariant);

    /**
     * Get dynamic parameters based on current page type
     * This function extracts page-specific data from Redux state
     */
    const getAdjustParams = (): Record<string, string> | undefined => {
        if (!routePageType) return undefined;

        switch (routePageType) {
            case 'vip':
                // For VIP page, get ad ID from adDetails
                if (adDetails?.id) {
                    return { adID: String(adDetails.id) };
                }
                break;
            // Add more page-specific logic here as needed
            // case 'srp':
            //     return { query: searchQuery };
            default:
                break;
        }
        return undefined;
    };
    // const isVisibleDefault = !isSafari && Boolean(pageConfig) && Boolean(pageConfig?.adjustLink) && isMobile;
    // const [isVisible, setIsVisible] = useState(isVisibleDefault);
    const isVisibleDefault =
        isExperimentEnabled && !isSafari && Boolean(pageConfig) && Boolean(pageConfig?.adjustLink) && isMobile;
    const [isDismissed, setIsDismissed] = useState(false);
    const shouldShow = isVisibleDefault && !isDismissed;

    const handleClose = () => {
        // setIsVisible(false);
        setIsDismissed(true);
    };

    const handleGetClick = () => {
        // Build Adjust deep link URL
        const adjustLinkConfig = pageConfig?.adjustLink;
        let adjustUrl = '';
        if (typeof adjustLinkConfig === 'function') {
            // If adjustLink is a function, call it with dynamic params and device OS
            const adjustParams = getAdjustParams();
            adjustUrl = adjustLinkConfig(adjustParams, deviceOS);
        } else {
            // If adjustLink is a string, use it directly
            adjustUrl = adjustLinkConfig || '';
        }

        // Track the click
        trackGA4Event({
            event: 'applink_banner_click',
            campaign: pageConfig?.campaign || 'Mobile_Web_Banner',
            adgroup: pageConfig?.adgroup || 'Home_Page',
        });

        // alert(adjustUrl);
        window.location.href = adjustUrl;
    };

    if(!shouldShow) {
        return null
    }

    return (
        <BannerContainer>
            <BannerCloseButton
                type="button"
                onClick={handleClose}
                aria-label="Close banner"
            >
                <Icon type="clear" size="small" />
            </BannerCloseButton>
            <BannerContentWrap>
                <BannerContent onClick={handleGetClick}>
                    <AppIcon>
                        <Icon css={gumtreeIconStyle} type="gumtree" />
                    </AppIcon>
                    <BannerText>
                        <BannerTitle>{title}</BannerTitle>
                        <BannerSubtitle>{subtitle}</BannerSubtitle>
                    </BannerText>
                </BannerContent>
                <GetButton type="button" onClick={handleGetClick}>
                    Get
                </GetButton>
            </BannerContentWrap>
        </BannerContainer>
    );
};

export default AppDownloadBanner;

